import { motion, AnimatePresence } from 'framer-motion'
import { useNotificationStore, type NotificationType } from '../stores/notificationStore'
import clsx from 'clsx'

const typeStyles: Record<NotificationType, { bg: string; border: string; icon: string; iconColor: string }> = {
  success: {
    bg: 'bg-signal-green/10',
    border: 'border-signal-green/30',
    icon: '✓',
    iconColor: 'text-signal-green',
  },
  error: {
    bg: 'bg-signal-red/10',
    border: 'border-signal-red/30',
    icon: '✕',
    iconColor: 'text-signal-red',
  },
  warning: {
    bg: 'bg-signal-amber/10',
    border: 'border-signal-amber/30',
    icon: '!',
    iconColor: 'text-signal-amber',
  },
  info: {
    bg: 'bg-signal-blue/10',
    border: 'border-signal-blue/30',
    icon: 'ℹ',
    iconColor: 'text-signal-blue',
  },
}

export function NotificationCenter() {
  const { notifications, remove } = useNotificationStore()

  return (
    <div
      className="fixed bottom-4 right-4 z-[500] flex flex-col gap-2 max-w-sm w-full pointer-events-none"
      role="region"
      aria-label="Notifications"
      aria-live="polite"
    >
      <AnimatePresence mode="popLayout">
        {notifications.map((notification) => {
          const style = typeStyles[notification.type]

          return (
            <motion.div
              key={notification.id}
              layout
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, x: 100, scale: 0.95 }}
              transition={{ type: 'spring', duration: 0.3, bounce: 0.1 }}
              className={clsx(
                'pointer-events-auto glass-card p-4 border shadow-lg',
                style.bg,
                style.border
              )}
            >
              <div className="flex items-start gap-3">
                {/* Icon */}
                <span
                  className={clsx(
                    'flex items-center justify-center w-6 h-6 rounded-full text-sm font-bold shrink-0',
                    style.bg,
                    style.iconColor
                  )}
                  aria-hidden="true"
                >
                  {style.icon}
                </span>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-star text-sm">
                    {notification.title}
                  </div>
                  {notification.message && (
                    <div className="text-star-muted text-xs mt-0.5 line-clamp-2">
                      {notification.message}
                    </div>
                  )}
                  {notification.action && (
                    <button
                      className={clsx(
                        'mt-2 text-xs font-medium hover:underline',
                        style.iconColor
                      )}
                      onClick={notification.action.onClick}
                    >
                      {notification.action.label}
                    </button>
                  )}
                </div>

                {/* Close button - 44px touch target for mobile */}
                <button
                  className="text-star-dim hover:text-star transition-colors shrink-0 p-2 -m-1 min-h-[44px] min-w-[44px] flex items-center justify-center"
                  onClick={() => remove(notification.id)}
                  aria-label="Dismiss notification"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Progress bar for auto-dismiss */}
              {notification.duration && notification.duration > 0 && (
                <motion.div
                  className={clsx('absolute bottom-0 left-0 h-0.5 rounded-b', style.iconColor.replace('text-', 'bg-'))}
                  initial={{ width: '100%' }}
                  animate={{ width: '0%' }}
                  transition={{ duration: (notification.duration ?? 0) / 1000, ease: 'linear' }}
                />
              )}
            </motion.div>
          )
        })}
      </AnimatePresence>
    </div>
  )
}
